import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Settings, FolderOpen, ChevronDown, Search, RefreshCw, Upload, X, Info, FileSpreadsheet, Workflow } from 'lucide-react';
import axios from 'axios';
import logger from '../../utils/logger';
import SetWorkflowModal from '../modals/SetWorkflowModal';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  Spinner,
} from '../ui';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const ALLOWED_MEDIA_EXTENSIONS = ['.mp4', '.mov', '.webm', '.mkv', '.avi', '.m4v', '.3gp', '.flv', '.wav', '.mp3', '.m4a', '.aac', '.flac', '.ogg', '.opus', '.wma', '.aiff', '.aif', '.alac', '.amr'];
const isAllowedMediaFile = (file) => {
  const ext = file.name.toLowerCase().replace(/^.*\./, '');
  const withDot = ext ? `.${ext}` : '';
  return ALLOWED_MEDIA_EXTENSIONS.some((e) => withDot === e || file.name.toLowerCase().endsWith(e));
};

/**
 * Generic content generation service component
 * Handles YouTube URL input, show selection, file uploads, prompt management
 * 
 * @param {Object} config - Configuration object with:
 *   - featureType: 'synopsis' | 'keyword' (for API endpoints)
 *   - featureName: Display name (e.g., 'Synopsis Generation', 'Keyword Generation')
 *   - icon: React component for the icon
 *   - color: Color scheme (e.g., 'sky', 'purple')
 *   - youtubeUrl, setYoutubeUrl: YouTube URL state
 *   - youtubeVideoInfo, setYoutubeVideoInfo: Video info state
 *   - extractingVideoInfo: Loading state
 *   - generatedContent: Generated content to display
 *   - currentProjectId: Current project ID
 *   - isProcessing: Processing state
 *   - handleProcessing: Processing handler function
 *   - handleViewProject: View project handler
 *   - setShowSettingsModal: Settings modal handler
 *   - fetchPrompt: Fetch prompt function
 *   - prompt, promptVersion, promptTotalVersions: Prompt state
 *   - selectedPromptVersionForUse: Selected prompt version
 *   - isEditingPrompt, editedPrompt: Editing state
 *   - promptLoading: Prompt loading state
 *   - setIsEditingPrompt, setEditedPrompt: Editing setters
 *   - handlePromptVersionChange: Prompt version change handler
 *   - savePrompt: Save prompt function
 *   - setSelectedPromptVersionForUse: Set selected version
 *   - fetchPromptVersion: Fetch prompt version function
 *   - selectedPromptId: Selected prompt ID
 *   - projectTypes: Array of project type options (optional)
 *   - defaultProjectType: Default project type
 *   - showComparisonOption: Whether to show comparison option (optional)
 */
const ContentGenerationService = ({
  config
}) => {
  const {
    featureType,
    featureName,
    icon: Icon,
    color = 'sky',
    youtubeUrl,
    setYoutubeUrl,
    youtubeVideoInfo,
    extractingVideoInfo,
    generatedContent,
    currentProjectId,
    isProcessing,
    handleProcessing,
    handleViewProject,
    setShowSettingsModal,
    fetchPrompt,
    prompt,
    promptVersion,
    promptTotalVersions,
    selectedPromptVersionForUse,
    isEditingPrompt,
    editedPrompt,
    promptLoading,
    setIsEditingPrompt,
    setEditedPrompt,
    handlePromptVersionChange,
    savePrompt,
    setSelectedPromptVersionForUse,
    fetchPromptVersion,
    selectedPromptId,
    defaultProjectType,
    showComparisonOption = false,
    defaultHasVideoAsset = true, // Default to true (Yes, upload MP4)
    user = null, // For keyword: PM/admin can assign task at creation
    transcriptionKnpFile = null,
    setTranscriptionKnpFile,
    transcriptionLanguage = 'ko-KR',
    setTranscriptionLanguage,
  } = config;

  const isPmOrAdmin = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  const canCreateTasks = isPmOrAdmin;

  const [selectedShow, setSelectedShow] = useState('');
  const [shows, setShows] = useState([]);
  const [filteredShows, setFilteredShows] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingShows, setLoadingShows] = useState(false);
  const [hasVideoAsset, setHasVideoAsset] = useState(defaultHasVideoAsset);
  const [mp4File, setMp4File] = useState(null);
  const [mp4FileName, setMp4FileName] = useState('');
  const [projectType] = useState(defaultProjectType || `${featureType}_generation`);
  const [referenceFile, setReferenceFile] = useState(null);
  const [referenceFileName, setReferenceFileName] = useState('');
  const [referenceText, setReferenceText] = useState('');
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const searchInputRef = useRef(null);

  const colorClasses = {
    sky: {
      bg: 'bg-sky-600',
      text: 'text-sky-600 dark:text-sky-400',
      border: 'border-sky-200 dark:border-sky-800',
      bgLight: 'bg-sky-50 dark:bg-sky-900/20',
      hover: 'hover:bg-sky-700',
      focus: 'focus:ring-sky-400',
    },
    purple: {
      bg: 'bg-purple-600',
      text: 'text-purple-600 dark:text-purple-400',
      border: 'border-purple-200 dark:border-purple-800',
      bgLight: 'bg-purple-50 dark:bg-purple-900/20',
      hover: 'hover:bg-purple-700',
      focus: 'focus:ring-purple-400',
    },
    design: {
      bg: 'bg-primary',
      text: 'text-primary',
      border: 'border-border',
      bgLight: 'bg-muted/30',
      hover: 'hover:bg-primary/90',
      focus: 'focus:ring-ring',
    },
  };

  const colors = colorClasses[color] || colorClasses.sky;

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (!isAllowedMediaFile(file)) {
        alert('Please upload a video or audio file (e.g. MP4, MOV, WEBM, M4A, MP3, WAV).');
        return;
      }
      if (file.size > 300 * 1024 * 1024) {
        alert('File is too large. Maximum size is 300MB.');
        return;
      }
      setMp4File(file);
      setMp4FileName(file.name);
      logger.log(`✅ Media file selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/webm': ['.webm'],
      'video/x-matroska': ['.mkv'],
      'video/x-msvideo': ['.avi'],
      'video/3gpp': ['.3gp'],
      'video/x-flv': ['.flv'],
      'audio/wav': ['.wav'],
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/aac': ['.aac'],
      'audio/flac': ['.flac'],
      'audio/ogg': ['.ogg'],
      'audio/opus': ['.opus'],
      'audio/x-ms-wma': ['.wma'],
      'audio/aiff': ['.aiff', '.aif'],
      'audio/amr': ['.amr'],
    },
    maxFiles: 1,
    multiple: false,
    disabled: !hasVideoAsset
  });

  const onDropReference = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const isText = file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt');
      
      if (!isText) {
        alert('Please upload a valid text (.txt) file');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setReferenceText(e.target.result);
        setReferenceFile(file);
        setReferenceFileName(file.name);
        logger.log(`✅ Reference file loaded: ${file.name}`);
      };
      reader.readAsText(file);
    }
  }, []);

  const { 
    getRootProps: getRefRootProps, 
    getInputProps: getRefInputProps, 
    isDragActive: isRefDragActive 
  } = useDropzone({
    onDrop: onDropReference,
    accept: {
      'text/plain': ['.txt']
    },
    maxFiles: 1,
    multiple: false
  });

  const onDropKnp = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0 && setTranscriptionKnpFile) setTranscriptionKnpFile(acceptedFiles[0]);
  }, [setTranscriptionKnpFile]);

  const {
    getRootProps: getKnpRootProps,
    getInputProps: getKnpInputProps,
    isDragActive: isKnpDragActive,
  } = useDropzone({
    onDrop: onDropKnp,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/csv': ['.csv'],
    },
    maxFiles: 1,
    multiple: false,
  });

  // Fetch shows list
  const fetchShows = async (forceRefresh = false) => {
    setLoadingShows(true);
    try {
      const token = localStorage.getItem('token');
      // Use synopsis/shows endpoint for now (can be shared or feature-specific)
      const url = forceRefresh 
        ? `${API_BASE}/synopsis/shows?force_refresh=true`
        : `${API_BASE}/synopsis/shows`;
      
      const response = await axios.get(url, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      
      if (response.data && response.data.shows) {
        const showsList = [...response.data.shows, 'Other'];
        setShows(showsList);
        setFilteredShows(showsList);
        logger.log(`✅ Loaded ${showsList.length} shows (including 'Other')${forceRefresh ? ' [refreshed]' : ''}`);
      }
    } catch (error) {
      logger.error('❌ Error fetching shows:', error);
      setShows(['Other']);
      setFilteredShows(['Other']);
    } finally {
      setLoadingShows(false);
    }
  };

  useEffect(() => {
    fetchShows(false);
    // Only fetch prompt for non-keyword features
    if (featureType !== 'keyword') {
      fetchPrompt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefreshShows = async () => {
    logger.log('🔄 Refreshing shows list from Neo4j...');
    await fetchShows(true);
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredShows(shows);
    } else {
      const filtered = shows.filter(show =>
        show.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (!filtered.includes('Other')) {
        filtered.push('Other');
      }
      setFilteredShows(filtered);
    }
  }, [searchQuery, shows]);

  useEffect(() => {
    if (showDropdown && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showDropdown]);

  const handleShowSelect = (show) => {
    setSelectedShow(show);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleStartProcessing = () => {
    // For keywords, don't pass prompt version (not used)
    const promptVersionToUse = featureType === 'keyword' || featureType === 'transcription' ? null : selectedPromptVersionForUse;
    const assigneeParam = undefined;
    const knpParam = featureType === 'transcription' ? (transcriptionKnpFile || null) : undefined;
    const languageParam = featureType === 'transcription' ? (transcriptionLanguage || 'ko-KR') : undefined;
    handleProcessing(
      youtubeUrl,
      youtubeVideoInfo,
      promptVersionToUse,
      null,
      null,
      null,
      selectedShow,
      hasVideoAsset ? mp4File : null,
      projectType,
      showComparisonOption && projectType === `${featureType}_comparison` ? referenceText : null,
      assigneeParam,
      knpParam,
      languageParam
    );
  };

  const isDisabled = !selectedShow ||
    isProcessing ||
    (!youtubeUrl.trim() && (!hasVideoAsset || !mp4File)) ||
    (hasVideoAsset && !mp4File) ||
    (showComparisonOption && projectType === `${featureType}_comparison` && !referenceFile);

  const pageTitle = showComparisonOption && projectType === `${featureType}_comparison`
    ? 'Comparison Analysis'
    : featureName;
  const pageDescription = showComparisonOption && projectType === `${featureType}_comparison`
    ? `Compare human and AI-generated ${featureType}s`
    : `Generate video ${featureType} from YouTube URLs`;

  if (!canCreateTasks) {
    return (
      <div key={`${featureType}-service-interface`} className="space-y-4">
        <div className="flex items-center">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-neutral-100">{pageTitle}</h3>
            <p className="text-gray-600 dark:text-neutral-400 mt-1">{pageDescription}</p>
          </div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-8 text-center">
          <p className="text-amber-800 dark:text-amber-200 font-medium mb-2">Only admin and PM can create new tasks.</p>
          <p className="text-gray-600 dark:text-neutral-400 text-sm">You can view existing projects from the sidebar (Synopsis Projects / Keyword Projects).</p>
        </div>
      </div>
    );
  }

  return (
    <div key={`${featureType}-service-interface`} className="space-y-4">
      {/* Page title and description - outside bordered frame */}
      <div className="flex items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-neutral-100">{pageTitle}</h3>
          <p className="text-gray-600 dark:text-neutral-400 mt-1">{pageDescription}</p>
        </div>
      </div>

      <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-8 shadow-sm">
        {featureType !== 'synopsis' && featureType !== 'keyword' && featureType !== 'transcription' && (
          <div className="flex items-center justify-end mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setShowSettingsModal(true);
                fetchPrompt();
              }}
              title="Settings"
            >
              <Settings className="h-6 w-6" />
            </Button>
          </div>
        )}

        {/* Project Type Selection - Switcher (design system) */}
        {/*projectTypes.length > 0 && (
          <div className="mb-6">
            <label className="text-foreground block text-sm font-medium mb-2">Project Type</label>
            <div className="border-border mt-2 flex flex-row flex-wrap gap-2 rounded-lg border p-1">
              {projectTypes.map((type) => (
                <Button
                  key={type.value}
                  variant={projectType === type.value ? 'secondary' : 'ghost'}
                  size="sm"
                  className="flex-1 min-w-0"
                  onClick={() => setProjectType(type.value)}
                >
                  {type.icon && <type.icon className="mr-2 h-4 w-4" />}
                  {type.label}
                </Button>
              ))}
            </div>
          </div>
        )*/}

        {/* Show Title (left) and Set Workflow (right) - two columns; horizontally aligned */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className={`flex flex-col ${hasVideoAsset ? 'md:col-span-1' : ''}`}>
            <div className="flex items-center gap-2 mb-2 min-h-8">
              <label htmlFor="showSelect" className="text-gray-700 dark:text-neutral-300 text-sm font-medium">
                Show Title <span className="text-red-500">*</span>
              </label>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={handleRefreshShows}
                disabled={loadingShows}
                title="Refresh shows list"
              >
                {loadingShows ? <Spinner size="sm" className="h-3.5 w-3.5 text-primary" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <DropdownMenu open={showDropdown} onOpenChange={(open) => { setShowDropdown(open); if (!open) setSearchQuery(''); }}>
              <DropdownMenuTrigger asChild>
                <Button variant="outlined" className="w-full justify-between font-normal" size="default">
                  <span className={selectedShow ? 'text-foreground' : 'text-muted-foreground'}>
                    {selectedShow || 'Select a show...'}
                  </span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[12rem] max-h-80 p-0">
                <div className="p-2 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search shows..."
                      className="w-full bg-muted/50 border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto p-1">
                  {loadingShows ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      <Spinner size="md" className="mx-auto text-primary" />
                      <p className="mt-2">Loading shows...</p>
                    </div>
                  ) : filteredShows.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">No shows found</div>
                  ) : (
                    filteredShows.map((show, index) => (
                      <DropdownMenuItem
                        key={index}
                        onSelect={() => handleShowSelect(show)}
                        className={selectedShow === show ? 'bg-accent' : ''}
                      >
                        {show}
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            {!selectedShow && (
              <p className="text-gray-500 dark:text-neutral-500 text-xs mt-1">Please select a show</p>
            )}
          </div>

          {featureType === 'transcription' ? (
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-2 min-h-8">
                <label className="text-gray-700 dark:text-neutral-300 text-sm font-medium">
                  Language
                </label>
              </div>
              <DropdownMenu open={languageDropdownOpen} onOpenChange={setLanguageDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outlined" className="w-full justify-between font-normal" size="default">
                    <span className="text-foreground">
                      {transcriptionLanguage === 'ko-KR' ? 'Korean' : transcriptionLanguage === 'en-US' ? 'English' : transcriptionLanguage}
                    </span>
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-200 ${languageDropdownOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)] p-0">
                  <DropdownMenuItem
                    onSelect={() => { setTranscriptionLanguage && setTranscriptionLanguage('ko-KR'); setLanguageDropdownOpen(false); }}
                  >
                    Korean
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => { setTranscriptionLanguage && setTranscriptionLanguage('en-US'); setLanguageDropdownOpen(false); }}
                  >
                    English
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (featureType === 'keyword' || featureType === 'synopsis') && canCreateTasks ? (
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-2 min-h-8">
                <span className="text-gray-700 dark:text-neutral-300 text-sm font-medium">Set Workflow</span>
              </div>
              <Button
                variant="outlined"
                size="default"
                className="w-full justify-center gap-2"
                onClick={() => setWorkflowModalOpen(true)}
                title="Set workflow for a project"
              >
                <Workflow className="h-4 w-4" /> Set Workflow
              </Button>
              <p className="text-gray-500 dark:text-neutral-500 text-xs mt-1">
                {currentProjectId ? 'Edit workflow for the current project' : 'Available in project list or project detail after creation'}
              </p>
              <SetWorkflowModal
                isOpen={workflowModalOpen}
                onClose={() => setWorkflowModalOpen(false)}
                projectId={currentProjectId || null}
                projectType={projectType}
                isLocked={false}
                onSaved={() => setWorkflowModalOpen(false)}
              />
            </div>
          ) : null}
        </div>

        {/* Video source: file upload vs YouTube URL - consolidated section below Show Title */}
        <div className="mb-6">
          <label className="text-foreground block text-sm font-medium mb-2">
            Do you have a video or audio file? <span className="text-muted-foreground text-xs">(Or use YouTube URL below)</span>
          </label>
          <RadioGroup
            value={hasVideoAsset ? 'mp4' : 'youtube'}
            onValueChange={(value) => {
              setHasVideoAsset(value === 'mp4');
              if (value === 'youtube') {
                setMp4File(null);
                setMp4FileName('');
              }
            }}
            className="mt-2 flex flex-row gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="mp4" id="videoSourceMp4" />
              <label htmlFor="videoSourceMp4" className="text-foreground cursor-pointer text-xs">
                Yes, upload file
              </label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="youtube" id="videoSourceYoutube" />
              <label htmlFor="videoSourceYoutube" className="text-foreground cursor-pointer text-xs">
                No, use YouTube URL only
              </label>
            </div>
          </RadioGroup>
          {!hasVideoAsset && (
            <div className="mt-3">
              <label htmlFor="youtubeUrl" className="text-foreground block text-sm font-medium mb-2">
                YouTube URL <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                id="youtubeUrl"
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                rounded="lg"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                Enter a valid YouTube video URL
              </p>
              {extractingVideoInfo && (
                <div className={`mt-3 flex items-center text-sm ${colors.text}`}>
                  <Spinner size="sm" className={`mr-2 ${colors.text}`} />
                  Extracting video information...
                </div>
              )}
            </div>
          )}
        </div>

        {/* Reference File Upload (Comparison Only) */}
        {showComparisonOption && projectType === `${featureType}_comparison` && (
          <div className="mb-6 animate-in fade-in slide-in-from-top-4 duration-300">
            <label className="block text-gray-700 dark:text-neutral-300 text-sm font-medium mb-2">
              Human {featureName} (Reference) <span className="text-red-500">*</span>
            </label>
            <div 
              {...getRefRootProps()} 
              className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-200 text-center cursor-pointer
                ${isRefDragActive 
                  ? `${colors.border} ${colors.bgLight} scale-[1.01]` 
                  : 'border-gray-200 dark:border-neutral-800 hover:border-gray-300 dark:hover:border-neutral-700 bg-gray-50/50 dark:bg-neutral-900/50'
                }
                ${referenceFile ? `${colors.bgLight} ${colors.border}` : ''}`}
            >
              <input {...getRefInputProps()} id="refUpload" />
              
              <div className="flex flex-col items-center">
                {referenceFile ? (
                  <div className={`flex items-center space-x-3 bg-white dark:bg-neutral-950 px-4 py-2 rounded-lg border ${colors.border} shadow-sm`}>
                    <div className={`${colors.bgLight} p-2 rounded-md`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-sm text-gray-900 dark:text-neutral-100 font-medium truncate max-w-xs">{referenceFileName}</span>
                      <span className="text-xs text-gray-500">{referenceText.length} characters</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="ml-2 rounded-full [&>svg]:text-muted-foreground hover:[&>svg]:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setReferenceFile(null);
                        setReferenceFileName('');
                        setReferenceText('');
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className={`w-6 h-6 mb-2 ${isRefDragActive ? colors.text : 'text-gray-400'}`} />
                    <p className="text-gray-900 dark:text-neutral-100 text-sm font-medium">
                      {isRefDragActive ? `Drop reference ${featureType} here` : `Upload reference ${featureType} (.txt)`}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Video/Audio Upload */}
        {hasVideoAsset && (
          <div className="mb-6">
            <label className="block text-gray-700 dark:text-neutral-300 text-sm font-medium mb-2">
              Upload video or audio <span className="text-red-500">*</span>
            </label>
            <div 
              {...getRootProps()} 
              className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 text-center cursor-pointer
                ${isDragActive 
                  ? `${colors.border} ${colors.bgLight} scale-[1.01]` 
                  : 'border-gray-200 dark:border-neutral-800 hover:border-gray-300 dark:hover:border-neutral-700 bg-gray-50/50 dark:bg-neutral-900/50'
                }
                ${mp4File ? `${colors.bgLight} ${colors.border}` : ''}`}
            >
              <input {...getInputProps()} id="mp4Upload" />
              
              <div className="flex flex-col items-center">
                {mp4File ? (
                  <div className={`flex items-center space-x-3 bg-white dark:bg-neutral-950 px-4 py-2 rounded-lg border ${colors.border} shadow-sm animate-in fade-in zoom-in duration-300`}>
                    <div className={`${colors.bgLight} p-2 rounded-md`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <span className="text-sm text-gray-900 dark:text-neutral-100 font-medium truncate max-w-xs">{mp4FileName}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="rounded-full [&>svg]:text-muted-foreground hover:[&>svg]:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMp4File(null);
                        setMp4FileName('');
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className={`p-4 rounded-full mb-4 transition-colors ${isDragActive ? colors.bgLight : 'bg-gray-100 dark:bg-neutral-800'}`}>
                      <Upload className={`w-8 h-8 ${isDragActive ? colors.text : 'text-gray-400'}`} />
                    </div>
                    <p className="text-gray-900 dark:text-neutral-100 font-medium mb-1">
                      {isDragActive ? 'Drop your video here' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-gray-500 dark:text-neutral-500 text-sm">
                      One file up to 300MB (e.g. MP4, MOV, WEBM, M4A, MP3, WAV)
                    </p>
                  </>
                )}
              </div>
            </div>
            <p className="text-gray-500 dark:text-neutral-500 text-xs mt-3 flex items-center">
              <Info className="w-3.5 h-3.5 mr-1.5" />
              The transcript will be extracted using Google Speech-to-Text for {featureType} generation.
            </p>
          </div>
        )}

        {/* Video Title Display */}
        {youtubeVideoInfo && !extractingVideoInfo && (
          <div className="mb-6 bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-6">
            <div className="flex items-start space-x-4">
              {youtubeVideoInfo.thumbnail && (
                <img 
                  src={youtubeVideoInfo.thumbnail} 
                  alt={youtubeVideoInfo.title}
                  className="w-32 h-24 object-cover rounded-lg flex-shrink-0"
                />
              )}
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">{youtubeVideoInfo.title}</h4>
                {youtubeVideoInfo.error ? (
                  <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">⚠️ {youtubeVideoInfo.error}</p>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-neutral-400 mt-1">Ready to process</p>
                )}
                
                {youtubeVideoInfo.transcript && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-neutral-300 flex items-center">
                        <Icon className="w-4 h-4 mr-2" />
                        Transcript (Extracted)
                      </h5>
                      <span className="text-xs text-gray-500 dark:text-neutral-500">
                        {typeof youtubeVideoInfo.transcript === 'object' 
                          ? youtubeVideoInfo.transcript.text?.length || 0 
                          : youtubeVideoInfo.transcript.length} characters
                      </span>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <p className="text-sm text-gray-700 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed">
                        {(() => {
                          const transcriptText = typeof youtubeVideoInfo.transcript === 'object' 
                            ? youtubeVideoInfo.transcript.text 
                            : youtubeVideoInfo.transcript;
                          return transcriptText && transcriptText.length > 500 
                            ? transcriptText.substring(0, 500) + '...'
                            : transcriptText;
                        })()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Generated Content Display */}
        {generatedContent && (
          <div className={`mb-6 ${colors.bgLight} border ${colors.border} rounded-lg p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h5 className={`text-sm font-medium ${colors.text} flex items-center`}>
                <Icon className="w-4 h-4 mr-2" />
                AI-Generated {featureName}:
              </h5>
              {currentProjectId && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleViewProject}
                >
                  <FolderOpen className="w-4 h-4" />
                  View Project
                </Button>
              )}
            </div>
            <div className={`bg-white dark:bg-neutral-950 border ${colors.border} rounded-lg p-4`}>
              <p className="text-sm text-gray-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                {generatedContent}
              </p>
            </div>
          </div>
        )}

        {/* Synopsis Generation Prompt - bottom of page, Admin and PM only (not for keyword or transcription) */}
        {featureType !== 'keyword' && featureType !== 'transcription' && isPmOrAdmin && (
          <div className="mb-6 mt-8 border-t border-gray-200 dark:border-neutral-800 pt-6">
            <div className="mb-4 flex items-center space-x-2">
              <label className="block text-gray-700 dark:text-neutral-300 text-sm font-medium">
                {featureName} Prompt (version {promptVersion})
              </label>
              <div className="group relative">
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-80 p-3 bg-gray-900 dark:bg-neutral-800 text-white dark:text-neutral-100 text-xs rounded-lg shadow-xl z-50">
                  <p className={`mb-2 font-semibold ${colors.text}`}>What does this do?</p>
                  <p className="leading-relaxed">
                    This prompt defines how the AI analyzes the YouTube video to generate a {featureType}.
                    You can customize the prompt to get specific results, and navigate version history to select the most effective version for your needs. You can edit the prompt after project is created too.
                  </p>
                  <div className="absolute bottom-0 left-4 -translate-x-1/2 translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900 dark:border-t-neutral-800" />
                </div>
              </div>
            </div>

            <div className="relative">
              {promptLoading ? (
                <div className="flex items-center justify-center py-12 bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg">
                  <Spinner size="lg" className={colors.text} />
                </div>
              ) : (
                <Textarea
                  value={isEditingPrompt ? editedPrompt : prompt}
                  onChange={(e) => setEditedPrompt(e.target.value)}
                  disabled={!isEditingPrompt}
                  placeholder={`Enter your ${featureType} prompt here...`}
                />
              )}
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500 dark:text-neutral-500">
                  Version {promptVersion} of {promptTotalVersions}
                </span>
                <div className="flex items-center rounded-xl overflow-hidden border border-border bg-background shadow-subtle-xs">
                  <Button
                    variant="secondary"
                    size="xs"
                    className="rounded-none border-r border-border"
                    onClick={() => handlePromptVersionChange('prev')}
                    disabled={promptVersion <= 1 || promptLoading}
                  >
                    &lt;
                  </Button>
                  <Button
                    variant="secondary"
                    size="xs"
                    className="rounded-none"
                    onClick={() => handlePromptVersionChange('next')}
                    disabled={promptVersion >= promptTotalVersions || promptLoading}
                  >
                    &gt;
                  </Button>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  onClick={async () => {
                    if (!selectedPromptId || selectedPromptVersionForUse !== promptVersion) {
                      await fetchPromptVersion(promptVersion);
                    }
                    setSelectedPromptVersionForUse(promptVersion);
                    alert(`Version ${promptVersion} will be used for the next ${featureType} generation.`);
                  }}
                >
                  {selectedPromptVersionForUse === promptVersion ? '✓ Using' : 'Use'}
                </Button>
              </div>
              {!isEditingPrompt ? (
                <Button
                  variant="link"
                  size="link"
                  className="text-sm font-medium"
                  onClick={() => {
                    setIsEditingPrompt(true);
                    setEditedPrompt(prompt);
                  }}
                >
                  Edit Prompt
                </Button>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsEditingPrompt(false);
                      setEditedPrompt(prompt);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={async () => {
                      await savePrompt();
                    }}
                  >
                    Save Changes
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transcription-only: Dictionary (Optional) - at bottom */}
        {featureType === 'transcription' && (
          <div className="mb-6">
            <label className="text-foreground mb-2 block text-sm font-medium">
              Dictionary <span className="text-muted-foreground text-xs">(Optional)</span>
            </label>
            <div
              {...getKnpRootProps()}
              className={`relative cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all duration-200 w-full ${
                isKnpDragActive
                  ? `${colors.border} ${colors.bgLight} scale-[1.01]`
                  : 'border-gray-200 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-900/50 hover:border-gray-300 dark:hover:border-neutral-700'
              } ${transcriptionKnpFile ? `${colors.bgLight} ${colors.border}` : ''}`}
            >
              <input {...getKnpInputProps()} id="transcriptionKnpFileUpload" />
              <div className="flex flex-col items-center">
                {transcriptionKnpFile ? (
                  <div className={`flex animate-in fade-in zoom-in duration-300 items-center space-x-3 rounded-lg border bg-white px-4 py-2 shadow-sm dark:bg-neutral-950 ${colors.border}`}>
                    <div className={`rounded-md p-2 ${colors.bgLight}`}>
                      <FileSpreadsheet className={`h-5 w-5 ${colors.text}`} />
                    </div>
                    <span className="max-w-xs truncate text-sm font-medium text-gray-900 dark:text-neutral-100">
                      {transcriptionKnpFile.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="rounded-full [&>svg]:text-muted-foreground hover:[&>svg]:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (setTranscriptionKnpFile) setTranscriptionKnpFile(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className={`mb-3 rounded-full p-3 transition-colors ${isKnpDragActive ? colors.bgLight : 'bg-gray-100 dark:bg-neutral-800'}`}>
                      <FileSpreadsheet className={`h-6 w-6 ${isKnpDragActive ? colors.text : 'text-gray-400 dark:text-neutral-500'}`} />
                    </div>
                    <p className="mb-1 text-sm font-medium text-gray-900 dark:text-neutral-100">
                      {isKnpDragActive ? 'Drop Dictionary here' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-neutral-500">
                      .xlsx or .csv with phrases column
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Start Processing Button */}
        <div className="mt-8 flex items-center justify-center">
          <Button
            variant="default"
            size="lg"
            onClick={handleStartProcessing}
            disabled={isDisabled}
          >
            {isProcessing ? (
              <>
                <Spinner size="md" className="mr-3 text-white" />
                Processing...
              </>
            ) : (
              <>
                <Icon className="w-5 h-5 mr-2" />
                Create Project
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContentGenerationService;
